import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Bell, Check, CheckCheck } from "lucide-react";

const POLL_MS = 60000;

const ICONS = {
  new_booking: "📩",
  new_venue_booking: "📩",
  boost_activated: "✨",
  report_resolved: "✔",
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return `il y a ${Math.floor(diff / 86400)} j`;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/me/notifications", { params: { limit: 15 } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {/* silent */}
  };

  useEffect(() => {
    if (!user) { setItems([]); setUnread(0); return; }
    load();
    const i = setInterval(load, POLL_MS);
    return () => clearInterval(i);
    // eslint-disable-next-line
  }, [user?.user_id]);

  // Click-outside close
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markAllRead = async () => {
    try {
      await api.post("/me/notifications/read", { all: true });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch {/* silent */}
  };

  const openItem = async (n) => {
    if (!n.read) {
      try {
        await api.post("/me/notifications/read", { ids: [n.id] });
        setUnread((u) => Math.max(0, u - 1));
        setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
      } catch {/* silent */}
    }
    setOpen(false);
    if (n.link) nav(n.link);
  };

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="notification-bell"
        title="Notifications"
        className="relative p-2 hover:bg-cream-100 rounded-md transition-colors"
      >
        <Bell className="h-4 w-4 text-ink/70" />
        {unread > 0 && (
          <span
            data-testid="notification-unread-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center text-[10px] font-bold bg-accent text-white rounded-full"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 top-full mt-2 w-[360px] bg-white border border-ink/10 rounded-md shadow-luxe-md overflow-hidden z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-ink/10">
            <div>
              <p className="text-label text-muted-foreground">Notifications</p>
              <p className="font-heading font-medium text-ink">{unread} non lue{unread > 1 ? "s" : ""}</p>
            </div>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                data-testid="notification-mark-all-read"
                className="text-[11px] font-semibold uppercase tracking-wider text-accent hover:text-ink flex items-center gap-1"
              >
                <CheckCheck className="h-3 w-3" /> Tout marquer lu
              </button>
            )}
          </div>
          <div className="max-h-[440px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="h-8 w-8 mx-auto text-ink/20 mb-2" />
                <p className="text-sm text-muted-foreground">Pas encore de notifications.</p>
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => openItem(n)}
                  data-testid={`notification-item-${n.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-ink/5 hover:bg-cream-50 transition-colors flex gap-3 ${
                    n.read ? "" : "bg-cream-50"
                  }`}
                >
                  <span className="text-lg leading-none">{ICONS[n.type] || "·"}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <p className={`text-sm truncate ${n.read ? "text-ink/70 font-medium" : "text-ink font-semibold"}`}>
                        {n.title}
                      </p>
                      {!n.read && <span className="h-2 w-2 rounded-full bg-accent flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">{n.body}</p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1.5">{timeAgo(n.created_at)}</p>
                  </div>
                </button>
              ))
            )}
          </div>
          {items.length > 0 && (
            <div className="px-4 py-2.5 border-t border-ink/10 bg-cream-50">
              <p className="text-[10px] text-muted-foreground text-center flex items-center justify-center gap-1">
                <Check className="h-3 w-3" /> Actualisé toutes les 60 s
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
